import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import { ModalOverlay, ModalContainer as BaseModalContainer } from './BaseModal';

const ModalContainer = styled(BaseModalContainer)`
  max-width: 900px;
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 2px solid #e5e7eb;
  background: linear-gradient(135deg, #F1AF59 0%, #e89b3e 100%);
  color: white;
  position: relative;

  h2 {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: bold;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 14px;
  }
`;

const ModalBody = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 20px 32px;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;

  &.primary {
    background: #F1AF59;
    color: white;

    &:hover {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #F1AF59;
    background: #fef9f3;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    font-size: 14px;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
    color: #6b7280;
  }
`;

const ErrorRow = styled.tr`
  background: ${props => props.$hasError ? '#fef2f2' : 'white'};
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-size: 12px;
`;

const SuccessText = styled.span`
  color: #10b981;
  font-size: 12px;
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #93c5fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #1e40af;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
`;

/**
 * Excel batch upload modal for students.
 * Allows downloading template and uploading Excel file with student data.
 */
const ExcelUploadModal = ({
  onClose,
  onCreate,
  campuses = []
}) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview'
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Download Excel template
  const downloadTemplate = () => {
    const templateData = [
      ['Name', 'Gender', 'Grade', 'School', 'Campus', 'ParentName', 'ParentEmail', 'ParentPhone', 'ParentAddress', 'AdditionalNotes'],
      ['John Smith', 'Male', '5', 'Sydney Grammar', campuses[0]?.name || 'City Campus', 'Jane Smith', 'jane@email.com', '0412345678', '123 Main St, Sydney', 'Allergic to peanuts'],
      ['Jane Doe', 'Female', '8', 'Knox Grammar', campuses[1]?.name || 'North Campus', 'John Doe', 'john@email.com', '0487654321', '456 Oak Ave, Melbourne', ''],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');

    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Name
      { wch: 10 }, // Gender
      { wch: 10 }, // Grade
      { wch: 25 }, // School
      { wch: 20 }, // Campus
      { wch: 25 }, // ParentName
      { wch: 30 }, // ParentEmail
      { wch: 15 }, // ParentPhone
      { wch: 40 }, // ParentAddress
      { wch: 40 }, // AdditionalNotes
    ];

    XLSX.writeFile(wb, 'student_upload_template.xlsx');
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Skip header row
        const rows = data.slice(1).filter(row => row.length > 0 && row[0]);

        const parsed = rows.map((row, index) => {
          const name = row[0]?.toString().trim() || '';
          const gender = row[1]?.toString().trim() || '';
          const gradeStr = row[2]?.toString().trim() || '';
          const grade = gradeStr ? parseInt(gradeStr) : null;
          const schoolName = row[3]?.toString().trim() || '';
          const campusName = row[4]?.toString().trim() || '';
          const parentName = row[5]?.toString().trim() || '';
          const parentEmail = row[6]?.toString().trim() || '';
          const parentPhone = row[7]?.toString().trim() || '';
          const parentAddress = row[8]?.toString().trim() || '';
          const additionalNotes = row[9]?.toString().trim() || '';

          // Find campus by name (case-insensitive)
          const campus = campuses.find(c =>
            c.name.toLowerCase() === campusName.toLowerCase()
          );

          // Validate gender
          const validGender = ['Male', 'Female'].includes(gender) ? gender : '';

          return {
            rowIndex: index + 2, // Excel row number (1-indexed, skip header)
            name,
            gender: validGender,
            grade,
            schoolName,
            campusId: campus?.id || '',
            campusName: campus?.name || campusName,
            parentName,
            parentEmail,
            parentPhone,
            parentAddress,
            additionalNotes,
            status: 'active',
          };
        });

        setParsedData(parsed);
        setStep('preview');
      } catch (err) {
        console.error('Error parsing Excel:', err);
        alert('Failed to parse Excel file. Please check the file format.');
      }
    };
    reader.readAsBinaryString(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validate all rows
  const validateRows = () => {
    const newErrors = {};
    parsedData.forEach((row, index) => {
      const rowErrors = [];
      if (!row.name) rowErrors.push('Name required');
      if (!row.campusId) rowErrors.push('Valid campus required');
      if (row.grade !== null && (row.grade < 1 || row.grade > 16 || isNaN(row.grade))) {
        rowErrors.push('Grade must be 1-16');
      }
      if (row.gender && !['Male', 'Female'].includes(row.gender)) {
        rowErrors.push('Gender must be Male or Female');
      }
      if (rowErrors.length > 0) {
        newErrors[index] = rowErrors;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle batch create
  const handleBatchCreate = async () => {
    if (!validateRows()) {
      alert('Please fix validation errors before uploading.');
      return;
    }

    setIsUploading(true);
    const results = { success: 0, failed: 0 };

    for (const row of parsedData) {
      try {
        await onCreate({
          name: row.name,
          gender: row.gender || null,
          grade: row.grade,
          schoolName: row.schoolName,
          campuses: row.campusId ? [row.campusId] : [],
          parentName: row.parentName,
          parentEmail: row.parentEmail,
          parentPhone: row.parentPhone,
          parentAddress: row.parentAddress,
          additionalNotes: row.additionalNotes,
          status: 'active'
        });
        results.success++;
      } catch (err) {
        console.error('Error creating student:', err);
        results.failed++;
      }
    }

    setIsUploading(false);
    alert(`Batch upload complete!\nSuccess: ${results.success}\nFailed: ${results.failed}`);

    if (results.success > 0) {
      onClose();
    }
  };

  const allHaveCampus = parsedData.every(row => row.campusId);
  const hasValidationErrors = Object.keys(errors).length > 0;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          <h2>Batch Upload Students</h2>
          <p>Upload an Excel file to create multiple students at once</p>
        </ModalHeader>

        <ModalBody>
          {step === 'upload' && (
            <>
              <InfoBox>
                <strong>Instructions:</strong>
                <ol style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                  <li>Download the Excel template using the button below</li>
                  <li>Fill in student information (Name, Gender, Grade, School, Campus, Parent info, Notes)</li>
                  <li>Campus names must match exactly (case-insensitive)</li>
                  <li>Upload the completed file and review before submitting</li>
                </ol>
              </InfoBox>

              <Button className="secondary" onClick={downloadTemplate} style={{ marginBottom: '20px' }}>
                Download Template
              </Button>

              <UploadArea onClick={() => fileInputRef.current?.click()}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
                <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Click to upload Excel file
                </div>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  Supports .xlsx and .xls files
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </UploadArea>
            </>
          )}

          {step === 'preview' && (
            <>
              <InfoBox>
                <strong>Preview:</strong> Review the data below. All students must have a valid campus before uploading.
              </InfoBox>

              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Gender</th>
                      <th>Grade</th>
                      <th>School</th>
                      <th>Campus *</th>
                      <th>Parent</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.map((row, index) => (
                      <ErrorRow key={index} $hasError={!!errors[index]}>
                        <td>{row.rowIndex}</td>
                        <td>
                          {row.name || <ErrorText>Missing</ErrorText>}
                        </td>
                        <td>{row.gender || '-'}</td>
                        <td>
                          {row.grade ? `Year ${row.grade}` : '-'}
                          {row.grade && (row.grade < 1 || row.grade > 16) && (
                            <ErrorText> (Invalid)</ErrorText>
                          )}
                        </td>
                        <td>{row.schoolName || '-'}</td>
                        <td>
                          {row.campusId ? (
                            <SuccessText>{row.campusName}</SuccessText>
                          ) : (
                            row.campusName ? (
                              <ErrorText>{row.campusName} (Not found)</ErrorText>
                            ) : <ErrorText>Missing</ErrorText>
                          )}
                        </td>
                        <td>{row.parentName || '-'}</td>
                        <td>
                          {errors[index] ? (
                            <ErrorText>{errors[index].join(', ')}</ErrorText>
                          ) : row.campusId ? (
                            <SuccessText>Ready</SuccessText>
                          ) : (
                            <ErrorText>Needs valid campus</ErrorText>
                          )}
                        </td>
                      </ErrorRow>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              {parsedData.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No data found in the uploaded file.
                </div>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <div>
            {step === 'preview' && (
              <Button className="secondary" onClick={() => setStep('upload')}>
                Back
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button className="secondary" onClick={onClose}>
              Cancel
            </Button>
            {step === 'preview' && (
              <Button
                className="primary"
                onClick={handleBatchCreate}
                disabled={!allHaveCampus || isUploading || parsedData.length === 0 || hasValidationErrors}
              >
                {isUploading ? 'Uploading...' : `Upload ${parsedData.length} Students`}
              </Button>
            )}
          </div>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ExcelUploadModal;
